import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../services/AuthContext';
import { getFullUrl, summariesAPI } from '../../services/api';

const ClientSidebar = ({ selectedCoach, activeTab }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [lastSummary, setLastSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    const fetchLatestSummary = async () => {
      if (!selectedCoach?.chatId) return;
      setLoadingSummary(true);
      try {
        const response = await summariesAPI.getAll({ coach_id: selectedCoach.chatId });
        const summaries = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.items || response.data?.data?.items || []);
        
        if (summaries && summaries.length > 0) {
          // Sort by created_at desc just in case
          summaries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          setLastSummary(summaries[0]);
        }
      } catch (err) {
        console.error('Error fetching latest summary:', err);
      } finally {
        setLoadingSummary(false);
      }
    };

    if (activeTab === 'video') {
      fetchLatestSummary();
    }
  }, [selectedCoach?.chatId, activeTab]);

  const navItems = [
    { id: 'video', label: 'Video Call', icon: 'videocam', path: `/user/video/${selectedCoach?.id}` },
    { id: 'chat', label: 'Text Chat', icon: 'forum', path: `/user/chat/${selectedCoach?.id}` },
    { id: 'voice', label: 'Voice Call', icon: 'mic', path: `/user/voice/${selectedCoach?.id}` },
    { id: 'sessions', label: 'Session History', icon: 'history', path: `/user/sessions/${selectedCoach?.id}` },
  ];

  return (
    <aside className="w-full lg:w-80 flex flex-col shrink-0 space-y-6">
      {/* 1. User Profile & System Nav Block */}
      <div className="bg-[var(--surface-white)] rounded-[2.5rem] shadow-xl shadow-black/5 border border-[var(--border-light)] overflow-hidden p-6 space-y-6">
        <section className="space-y-4">
          <h3 className="text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest flex items-center gap-2 px-1">
            <span className="material-symbols-outlined text-base">person</span>
            My Account
          </h3>
          <div className="flex items-center gap-4 p-4 rounded-3xl bg-[var(--off-white)] border border-[var(--border-light)] transition-all hover:bg-[var(--surface-white)] hover:shadow-md hover:border-orange-100 group">
            <div className="w-14 h-14 rounded-2xl overflow-hidden border-4 border-[var(--surface-white)] shadow-sm shrink-0 bg-[var(--surface-white)] flex items-center justify-center transition-transform group-hover:scale-110">
              {user?.avatarUrl ? (
                <img src={getFullUrl(user.avatarUrl)} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="material-symbols-outlined text-gray-300">person</span>
              )}
            </div>
            <div className="min-w-0">
              <p className="font-black text-[var(--text-primary)] truncate leading-none mb-1">{user?.name || 'User'}</p>
              <p className="text-[10px] font-bold text-[var(--tangerine)] uppercase tracking-widest">Premium Client</p>
            </div>
          </div>
        </section>

        <nav className="space-y-1">
          <Link
            to="/user/coaches"
            className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl font-bold transition-all ${
              location.pathname === '/user/coaches' 
                ? 'bg-[var(--tangerine)]/10 text-[var(--tangerine)] shadow-sm border border-[var(--tangerine)]/20' 
                : 'text-[var(--text-secondary)] hover:bg-[var(--off-white)] hover:text-gray-700'
            }`}
          >
            <span className={`material-symbols-outlined ${location.pathname === '/user/coaches' ? 'filled-icon' : ''}`}>explore</span>
            <span className="text-sm">Find Coaches</span>
          </Link>
          
          <Link
            to="/user/settings"
            className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl font-bold transition-all ${
              location.pathname === '/user/settings' 
                ? 'bg-[var(--tangerine)]/10 text-[var(--tangerine)] shadow-sm border border-[var(--tangerine)]/20' 
                : 'text-[var(--text-secondary)] hover:bg-[var(--off-white)] hover:text-gray-700'
            }`}
          >
            <span className={`material-symbols-outlined ${location.pathname === '/user/settings' ? 'filled-icon' : ''}`}>settings</span>
            <span className="text-sm">Account Settings</span>
          </Link>

          <Link
            to="/user/billing"
            className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl font-bold transition-all ${
              location.pathname === '/user/billing' 
                ? 'bg-[var(--tangerine)]/10 text-[var(--tangerine)] shadow-sm border border-[var(--tangerine)]/20' 
                : 'text-[var(--text-secondary)] hover:bg-[var(--off-white)] hover:text-gray-700'
            }`}
          >
            <span className={`material-symbols-outlined ${location.pathname === '/user/billing' ? 'filled-icon' : ''}`}>credit_card</span>
            <span className="text-sm">Billing</span>
          </Link>
        </nav>
      </div>

      {/* 2. Selected Coach Context Block */}
      {selectedCoach && (
        <div className="bg-[var(--surface-white)] rounded-[2.5rem] shadow-xl shadow-black/5 border border-[var(--border-light)] overflow-hidden p-6 space-y-6 animate-fade-in">
          {activeTab === 'video' ? (
            <section className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-[10px] font-black text-[var(--text-secondary)]/60 uppercase tracking-[0.2em] flex items-center gap-2 px-1">
                  <span className="material-symbols-outlined text-base">assignment_turned_in</span>
                  Key Decisions
                </h3>
                <div className="space-y-2">
                  {lastSummary?.summary_metadata?.key_decisions?.length > 0 ? (
                    lastSummary.summary_metadata.key_decisions.map((decision, idx) => (
                      <div key={idx} className="p-4 bg-[var(--off-white)] rounded-2xl border border-[var(--border-light)] text-[11px] text-[var(--text-primary)] leading-relaxed prose prose-sm max-w-none">
                        <ReactMarkdown>{decision}</ReactMarkdown>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 bg-[var(--off-white)]/50 rounded-2xl border border-dashed border-[var(--border-light)] text-center">
                      <p className="text-[10px] font-bold text-[var(--text-secondary)]/40 uppercase tracking-widest italic">
                        {loadingSummary ? 'Loading...' : 'No decisions recorded yet'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-[10px] font-black text-[var(--text-secondary)]/60 uppercase tracking-[0.2em] flex items-center gap-2 px-1">
                  <span className="material-symbols-outlined text-base">send</span>
                  Coach Follow-up
                </h3>
                <div className="p-4 bg-[var(--off-white)] rounded-2xl border border-[var(--border-light)] min-h-[80px]">
                  {lastSummary?.summary_metadata?.coach_follow_up ? (
                    <div className="text-[11px] text-[var(--text-primary)] leading-relaxed prose prose-sm max-w-none">
                       <ReactMarkdown>{lastSummary.summary_metadata.coach_follow_up}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-[10px] font-bold text-[var(--text-secondary)]/40 uppercase tracking-widest italic text-center py-4">
                       {loadingSummary ? 'Loading...' : 'No follow-up notes yet'}
                    </p>
                  )}
                </div>
              </div>
            </section>
          ) : (
            <section className="space-y-4">
              <h3 className="text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest flex items-center gap-2 px-1">
                <span className="material-symbols-outlined text-base">psychology</span>
                Coaching Context
              </h3>
              
              <div className="p-6 rounded-[2rem] border border-[var(--border-light)] bg-gradient-to-b from-[var(--surface-white)] to-[var(--off-white)]/50 text-center relative group shadow-inner">
                <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 border-[var(--surface-white)] shadow-lg mb-4 relative ring-4 ring-[var(--off-white)]/50 bg-[var(--off-white)] flex items-center justify-center transition-transform group-hover:rotate-3">
                  {selectedCoach.avatarUrl ? (
                    <img 
                      src={getFullUrl(selectedCoach.avatarUrl)}
                      alt={selectedCoach.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="material-symbols-outlined text-4xl text-gray-200">person</span>
                  )}
                </div>
                <h4 className="font-black text-[var(--text-primary)] text-lg tracking-tight mb-0.5">{selectedCoach.name}</h4>
                <p className="text-[10px] font-bold text-[var(--tangerine)] uppercase tracking-[0.15em] mb-4">{selectedCoach.specialty || selectedCoach.specialization || 'Expert Coach'}</p>
                
                <Link 
                  to={`/user/coach/${selectedCoach.id}`}
                  className="text-[10px] font-bold text-[var(--text-secondary)]/60 hover:text-[var(--tangerine)] underline decoration-dotted transition-colors"
                >
                  Profile & Details
                </Link>
              </div>
            </section>
          )}

          <nav className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname.includes(item.id) || activeTab === item.id;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl font-bold transition-all ${
                    isActive 
                      ? 'bg-[var(--tangerine)] text-white shadow-lg shadow-orange-500/20' 
                      : 'text-[var(--text-secondary)] hover:bg-[var(--off-white)] hover:text-gray-700'
                  }`}
                >
                  <span className={`material-symbols-outlined ${isActive ? 'filled-icon' : ''}`}>{item.icon}</span>
                  <span className="text-sm">{item.label}</span>
                  {isActive && (
                    <span className="ml-auto material-symbols-outlined text-white text-xs">chevron_right</span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* Quick Help Block */}
      <div className="bg-gradient-to-br from-[var(--tangerine)]/10 to-[var(--surface-white)] rounded-[2.5rem] p-8 border border-[var(--tangerine)]/20 text-center shadow-lg shadow-[var(--tangerine)]/5 mt-auto">
        <div className="w-12 h-12 bg-[var(--surface-white)] rounded-2xl shadow-sm border border-[var(--border-light)] flex items-center justify-center mx-auto mb-4">
          <span className="material-symbols-outlined text-[var(--tangerine)]">support_agent</span>
        </div>
        <h5 className="text-sm font-black text-[var(--text-primary)] mb-2 tracking-tight">Need Assistance?</h5>
        <p className="text-xs text-[var(--text-secondary)] leading-relaxed mb-6">
          Our support team is available 24/7 to help you with your coaching journey.
        </p>
        <button className="w-full bg-[var(--surface-white)] border border-[var(--border-light)] text-[var(--tangerine)] text-[10px] font-black uppercase tracking-widest py-3.5 rounded-2xl shadow-sm hover:shadow-md hover:border-[var(--border-light)] transition-all">
          Contact Support
        </button>
      </div>

    </aside>
  );
};

export default ClientSidebar;
